import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Info, Mic, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import LockedButton from "@/components/LockedButton";
import LockedFeatureSheet from "@/components/LockedFeatureSheet";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useCollections } from "@/hooks/useCollections";
import { useCaptureContext } from "@/contexts/CaptureContext";
import type { CaptureType } from "@/contexts/CaptureContext";
import type { CardContext } from "@/types/cardContext";

interface CaptureTypePickerProps {
  onSelect: (type: CaptureType) => void;
  cardContext: CardContext | null;
  onCardLeave: () => void;
  collectionId?: string | null;
  collectionName?: string;
  collectionIcon?: string;
  collectionColor?: string;
}

type CaptureTooltip = { title: string; what: string; ideas: string[] };

function CaptureTileSkeleton() {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-2 rounded-2xl border border-border/60 bg-card py-5 px-3 animate-pulse">
      <div className="h-8 w-8 rounded-full bg-muted" />
      <div className="h-3 w-14 rounded bg-muted" />
    </div>
  );
}

function CaptureTile({
  emoji,
  label,
  size = "default",
  variant = "default",
  onClick,
  tooltip,
}: {
  emoji: string;
  label: string;
  size?: "default" | "sm";
  variant?: "default" | "primary";
  onClick: () => void;
  tooltip?: CaptureTooltip;
}) {
  const { t } = useTranslation();
  const isSm = size === "sm";
  const baseClasses = isSm
    ? "flex w-full flex-col items-center justify-center gap-1 rounded-2xl border bg-card py-2.5 px-2 shadow-sm transition-all active:scale-95 hover:shadow-md"
    : "flex w-full flex-col items-center justify-center gap-1.5 rounded-2xl border bg-card py-4 px-3 shadow-sm transition-all active:scale-95 hover:shadow-md";
  const borderClasses =
    variant === "primary"
      ? "border-primary/40 hover:border-primary/60"
      : "border-border/60 hover:border-primary/40";
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onClick}
        className={`${baseClasses} ${borderClasses}`}
      >
        <span className={isSm ? "text-xl leading-none" : "text-3xl leading-none"}>{emoji}</span>
        <span className={isSm ? "text-[12px] font-medium text-foreground" : "text-[13px] font-medium text-foreground"}>{label}</span>
      </button>
      {tooltip && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-muted/90 text-muted-foreground hover:bg-muted transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Info className="h-3 w-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="top"
            align="center"
            className="w-56 space-y-2 px-4 py-3 rounded-xl text-xs"
          >
            <p className="font-semibold text-foreground">{tooltip.title}</p>
            <p className="text-muted-foreground leading-relaxed">{tooltip.what}</p>
            <div className="space-y-0.5">
              <p className="font-medium text-foreground">{t("recording.tooltip.ideas")}</p>
              {tooltip.ideas.map((idea, i) => (
                <p key={i} className="text-muted-foreground">
                  • {idea}
                </p>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

function SpecialtyTile({ expanded, onClick }: { expanded: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        expanded
          ? "flex w-full flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-primary/40 bg-primary/10 py-4 px-3 transition-all active:scale-95 hover:shadow-sm"
          : "flex w-full flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed border-border/80 bg-muted/40 py-4 px-3 transition-all active:scale-95 hover:border-border hover:bg-muted/60"
      }
    >
      <span className="text-3xl leading-none">✨</span>
      <span className="flex items-center gap-1 text-[13px] font-medium text-foreground">
        {t("capture.section.specialty")}
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
      </span>
    </button>
  );
}

export default function CaptureTypePicker({
  onSelect,
  cardContext,
  onCardLeave,
  collectionId,
  collectionName,
  collectionIcon,
  collectionColor,
}: CaptureTypePickerProps) {
  const { t } = useTranslation();
  const {
    canDrawing,
    canMapPin,
    canVoiceMemory,
    isLoading: entitlementsLoading,
  } = useEntitlements();

  const CAPTURE_TOOLTIPS: Record<string, CaptureTooltip> = {
    drawing: {
      title: t("recording.tooltip.drawing.title"),
      what: t("recording.tooltip.drawing.what"),
      ideas: t("recording.tooltip.drawing.ideas", { returnObjects: true }) as string[],
    },
    capsule: {
      title: t("recording.tooltip.capsule.title"),
      what: t("recording.tooltip.capsule.what"),
      ideas: t("recording.tooltip.capsule.ideas", { returnObjects: true }) as string[],
    },
  };
  const { activeCollection, stopActiveMode } = useCollections();
  const { data: captureData, optOutOfActiveCollection } = useCaptureContext();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [showLeaveCardDialog, setShowLeaveCardDialog] = useState(false);
  const [specialtyExpanded, setSpecialtyExpanded] = useState(false);
  const [tripSheetOpen, setTripSheetOpen] = useState(false);

  // Once the user has opted out for this capture, the "Adding to …" banner is
  // misleading — hide it. `data.collectionId === null` is the explicit opt-out
  // signal dispatched by the cancel dialog's "Just this memory" action.
  const optedOutThisCapture =
    "collectionId" in captureData && captureData.collectionId === null;
  const showActiveBanner =
    !collectionId && !!activeCollection && !optedOutThisCapture;

  return (
    <>
      <div className="flex flex-col gap-4 w-full px-5 pt-2">
        <div className="flex items-center min-h-[36px] pr-10">
          <p className="text-sm font-semibold text-foreground">
            {t("recording.typePicker.heading")}
          </p>
        </div>

        {/* The legacy "Start a memory collection" tile has been retired.
            The "+ New collection" entry now lives inside the metadata step's
            CollectionPicker, which opens CollectionTemplateBrowser directly. */}

        {/* Collection banner — shown when capture was opened from a specific collection; takes priority over the active-capture banner */}
        {collectionId && collectionName ? (
          <div
            className="w-full flex items-center gap-3 rounded-2xl px-4 py-3"
            style={{
              backgroundColor: collectionColor ? `${collectionColor}20` : "#F5F0E8",
              border: `1.5px solid ${collectionColor ? `${collectionColor}50` : "#D4C5A9"}`,
            }}
          >
            {collectionIcon && <span className="text-2xl leading-none">{collectionIcon}</span>}
            <div className="text-left">
              <p className="text-[10px] font-medium leading-tight" style={{ color: collectionColor ?? "#6B5B3E", opacity: 0.7 }}>
                {t("recording.typePicker.addingTo")}
              </p>
              <p className="text-sm font-bold leading-tight" style={{ color: collectionColor ?? "#3D2B1F" }}>
                {collectionName}
              </p>
            </div>
          </div>
        ) : (
          /* Active-capture banner — full width, prominent */
          showActiveBanner && activeCollection && (
            <button
              type="button"
              onClick={() => setCancelDialogOpen(true)}
              className="w-full flex items-center justify-between rounded-2xl px-4 py-3 transition-opacity hover:opacity-80 active:scale-[0.98]"
              style={{
                backgroundColor: `${activeCollection.color}1A`,
                color: activeCollection.color,
                border: `1.5px solid ${activeCollection.color}66`,
              }}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl leading-none">{activeCollection.icon}</span>
                <div className="text-left">
                  <p className="text-[10px] font-medium opacity-70 leading-tight">{t("recording.typePicker.addingTo")}</p>
                  <p className="text-sm font-bold leading-tight">{activeCollection.name}</p>
                </div>
              </div>
              <span className="text-[10px] opacity-60">{t("recording.typePicker.tapToManage")}</span>
            </button>
          )
        )}

        {/* Card context banner */}
        {cardContext && (
          <div
            className="w-full rounded-xl relative"
            style={{ backgroundColor: "#EEEDFE", border: "0.5px solid #AFA9EC", padding: 14 }}
          >
            <button
              onClick={() => setShowLeaveCardDialog(true)}
              className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full text-[#7F77DD] hover:bg-[#DDD9F7] transition-colors"
              style={{ fontSize: 14, lineHeight: 1 }}
            >
              x
            </button>
            <p style={{ fontSize: 13, fontWeight: 500, color: "#3C3489", paddingRight: 20 }}>
              {activeCollection?.icon ?? "🎯"} {cardContext.title}
            </p>
            <div style={{ maxHeight: 120, overflowY: "auto", marginTop: 4 }}>
              <p style={{ fontSize: 13, color: "#534AB7", lineHeight: 1.45 }}>
                {cardContext.body}
              </p>
              {cardContext.instructions && (
                <p style={{ fontSize: 13, color: "#534AB7", marginTop: 8 }}>
                  {cardContext.instructions}
                </p>
              )}
              {cardContext.tip && (
                <p style={{ fontSize: 12, color: "#7F77DD", marginTop: 8 }}>
                  {cardContext.tip}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Primary 2×2 grid — 3 keepsake types + a Specialty toggle tile */}
        <div className="grid grid-cols-2 gap-3 w-full">
          {entitlementsLoading ? (
            <CaptureTileSkeleton />
          ) : !canVoiceMemory ? (
            <LockedButton locked feature="voice_cap" className="block">
              <CaptureTile
                emoji="📼"
                label={t("capture.type.voice_moment")}
                variant="primary"
                onClick={() => {}}
              />
            </LockedButton>
          ) : (
            <CaptureTile
              emoji="📼"
              label={t("capture.type.voice_moment")}
              variant="primary"
              onClick={() => onSelect("voice_moment")}
            />
          )}
          <CaptureTile
            emoji="📷"
            label={t("recording.typePicker.photo")}
            onClick={() => onSelect("photo")}
          />
          {/* Shortie — locked for every tier (deferred feature).
              Per-tier limits will return when video infrastructure ships. */}
          <LockedButton locked feature="video_cap" className="block">
            <CaptureTile
              emoji="🎬"
              label={t("recording.typePicker.shortie")}
              onClick={() => {}}
            />
          </LockedButton>
          <SpecialtyTile
            expanded={specialtyExpanded}
            onClick={() => setSpecialtyExpanded((v) => !v)}
          />
        </div>

        {/* Inline specialty section — rendered only when the ✨ tile is active */}
        {specialtyExpanded && (
          <div className="grid grid-cols-3 gap-3 w-full animate-page-enter">
            {entitlementsLoading ? (
              <CaptureTileSkeleton />
            ) : !canDrawing ? (
              <LockedButton locked feature="drawing" className="block">
                <CaptureTile emoji="🎨" label={t("recording.typePicker.drawing")} size="sm" onClick={() => {}} />
              </LockedButton>
            ) : (
              <CaptureTile
                emoji="🎨"
                label={t("recording.typePicker.drawing")}
                size="sm"
                onClick={() => onSelect("drawing")}
                tooltip={CAPTURE_TOOLTIPS.drawing}
              />
            )}

            {/* Time Capsule — locked for every tier (deferred feature).
                Per-tier limits will return when the feature ships. */}
            <LockedButton locked feature="time_capsule" className="block">
              <CaptureTile emoji="🕰️" label={t("recording.typePicker.capsule")} size="sm" onClick={() => {}} />
            </LockedButton>

            {/* Map Pin — entitlement-gated (Keeper+). Keeper/Storyteller get a
                clean tile; tapping opens the "coming soon" teaser because the
                feature itself isn't live yet. Seedling sees a locked teaser. */}
            {entitlementsLoading ? (
              <CaptureTileSkeleton />
            ) : canMapPin ? (
              <CaptureTile
                emoji="🗺️"
                label={t("recording.typePicker.trip")}
                size="sm"
                onClick={() => setTripSheetOpen(true)}
              />
            ) : (
              <LockedButton locked feature="trip" className="block">
                <CaptureTile emoji="🗺️" label={t("recording.typePicker.trip")} size="sm" onClick={() => {}} />
              </LockedButton>
            )}
          </div>
        )}

        {/* Tell Mori — primary action card matching the grid tile styling */}
        <button
          type="button"
          onClick={() => onSelect("general_memory")}
          className="flex w-full items-center gap-3 rounded-2xl border border-border/60 bg-card px-5 py-5 shadow-sm transition-all active:scale-[0.98] hover:border-primary/40 hover:shadow-md mt-2"
        >
          <span className="text-xl leading-none shrink-0">💬</span>
          <span className="flex-1 text-left text-[13px] font-medium text-foreground truncate">
            {t("capture.tellMoriPlaceholder")}
          </span>
          <Mic className="h-6 w-6 text-primary shrink-0" />
        </button>

      </div>

      {/* Leave card confirmation */}
      {showLeaveCardDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6">
          <div className="w-full max-w-xs rounded-2xl bg-card p-5 space-y-3 shadow-lg">
            <p className="text-base font-medium text-foreground">
              {t("recording.leaveCard.title", { title: cardContext?.title })}
            </p>
            <p className="text-sm text-muted-foreground">
              {t("recording.leaveCard.body")}
            </p>
            <div className="flex gap-2 pt-1">
              <Button className="flex-1" onClick={() => setShowLeaveCardDialog(false)}>{t("recording.leaveCard.stay")}</Button>
              <Button variant="ghost" className="flex-1 text-muted-foreground" onClick={() => { onCardLeave(); setShowLeaveCardDialog(false); }}>{t("recording.leaveCard.leave")}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel hoarding dialog — triggered from the "Adding to …" banner */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("collections.cancelActiveMode.title", {
                name: activeCollection?.name ?? "",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("collections.cancelActiveMode.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <AlertDialogAction
              onClick={() => {
                optOutOfActiveCollection();
                setCancelDialogOpen(false);
              }}
            >
              {t("collections.cancelActiveMode.justThisMemory")}
            </AlertDialogAction>
            <AlertDialogAction
              disabled={cancelBusy}
              onClick={async (e) => {
                e.preventDefault();
                if (!activeCollection || cancelBusy) return;
                setCancelBusy(true);
                try {
                  await stopActiveMode(activeCollection.id);
                  optOutOfActiveCollection();
                  toast.success(t("collections.cancelActiveMode.stoppedToast"));
                  setCancelDialogOpen(false);
                } catch (err) {
                  console.error("[CaptureTypePicker] stopActiveMode failed", err);
                  toast.error(t("collections.cancelActiveMode.stopError"));
                } finally {
                  setCancelBusy(false);
                }
              }}
            >
              {t("collections.cancelActiveMode.stopAll")}
            </AlertDialogAction>
            <AlertDialogCancel>
              {t("collections.cancelActiveMode.keep")}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Coming-soon sheet for Map Pin on entitled tiers — reuses the same
          copy shown to Seedling via LockedButton, minus the lock icon. */}
      <LockedFeatureSheet
        feature="trip"
        open={tripSheetOpen}
        onClose={() => setTripSheetOpen(false)}
      />
    </>
  );
}
